"use client";

import React, { useState } from "react";
import { Link } from "@/components/common/Link";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  VariableProductAddToCart,
  AddToCartButton,
  ProductPrice,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { CircleCheck as CheckCircle2, ShieldCheck, Truck, Wrench, ChevronRight, PackageCheck, Share2 } from 'lucide-react';

declare global {
  interface Window {
    wvcClient?: {
      getCurrentWPQueryParams?: () => Record<string, any>;
      getCurrentWPQuery?: () => Record<string, any>;
    };
  }
}

function ProductDetailConsumer() {
  const { products, loading, error, refetch } = useEcommerceProducts();
  const [activeImage, setActiveImage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"description" | "specs">("description");

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="h-6 w-48 animate-pulse rounded bg-muted/60 mb-8" />
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="aspect-square w-full animate-pulse rounded-2xl bg-muted/50" />
            <div className="flex gap-3">
              {[0, 1, 2, 3].map((idx) => (
                <div
                  key={idx}
                  data-index={idx}
                  className="size-20 animate-pulse rounded-lg bg-muted/40"
                />
              ))}
            </div>
          </div>
          <div className="space-y-6">
            <div className="h-10 w-3/4 animate-pulse rounded bg-muted/60" />
            <div className="h-8 w-1/3 animate-pulse rounded bg-muted/50" />
            <div className="h-24 w-full animate-pulse rounded bg-muted/30" />
            <div className="h-12 w-full animate-pulse rounded bg-muted/50" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !products || products.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="text-lg font-medium text-muted-foreground">
          No se pudo cargar la información del producto.
        </p>
        <div className="mt-6 flex justify-center gap-4">
          <Button variant="outline" onClick={() => refetch()}>
            Reintentar
          </Button>
          <Button asChild>
            <Link to="/ShopPage">Ir a la tienda</Link>
          </Button>
        </div>
      </div>
    );
  }

  const product = products[0];
  const gallery = product.gallery_image_urls || [];
  const mainImage =
    activeImage ?? product.featured_image_url ?? gallery[0] ?? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1753482/img/placeholder.webp";

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Breadcrumb Navigation */}
      <div className="mb-8">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/HomePage">Inicio</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/ShopPage">Tienda</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {product.categories && product.categories.length > 0 && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{product.categories[0].name}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-semibold text-foreground max-w-[200px] sm:max-w-xs truncate">
                {product.title}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Main Product Showcase */}
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
        {/* Gallery / Image Column */}
        <div className="lg:col-span-6 space-y-4">
          <div className="relative aspect-square overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <img
              src={mainImage}
              alt={product.title}
              className="h-full w-full object-cover object-center transition-transform duration-300 hover:scale-105"
              loading="lazy"
            />
            {product.on_sale && (
              <Badge className="absolute top-4 left-4 bg-chart-1 text-primary-foreground font-semibold px-3 py-1 shadow-sm">
                Oferta Especial
              </Badge>
            )}
          </div>

          {/* Adaptive Thumbnail Strip */}
          {gallery.length > 1 && (
            <div
              className="flex flex-wrap gap-3"
              role="group"
              aria-label="Imágenes del producto"
            >
              {gallery.map((imgUrl, i) => {
                const isSelected = imgUrl === mainImage;
                return (
                  <Button
                    key={imgUrl + i}
                    data-index={i}
                    type="button"
                    variant="outline"
                    onClick={() => setActiveImage(imgUrl)}
                    aria-label={`${product.title} vista ${i + 1}`}
                    aria-current={isSelected ? "true" : undefined}
                    className={`size-20 p-0 overflow-hidden rounded-xl border-2 transition-all ${
                      isSelected
                        ? "border-primary ring-2 ring-primary/20 scale-105"
                        : "border-border opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={imgUrl}
                      alt=""
                      aria-hidden="true"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </Button>
                );
              })}
            </div>
          )}
        </div>

        {/* Product Details & Actions */}
        <div className="lg:col-span-6 flex flex-col justify-between">
          <div className="space-y-6">
            {/* Header / Badges */}
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="font-normal text-xs uppercase tracking-wider">
                  Garantía Servicios de Reparaciones
                </Badge>
                {product.is_in_stock !== false ? (
                  <Badge variant="outline" className="text-xs border-emerald-600/30 text-emerald-700 bg-emerald-50">
                    <CheckCircle2 className="size-3 text-emerald-600" />
                    En Stock y Listo para Entrega
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs border-destructive/30 text-destructive bg-destructive/10">
                    Bajo Pedido
                  </Badge>
                )}
              </div>
              <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                {product.title}
              </h1>
            </div>

            {/* Price Row */}
            <div className="flex items-baseline gap-3">
              <ProductPrice
                product={product}
                as="div"
                className="text-3xl font-bold text-foreground"
              />
              <span className="text-xs text-muted-foreground">IVA incluido</span>
            </div>

            {/* Short Description */}
            {product.short_description ? (
              <div
                className="text-base text-muted-foreground leading-relaxed prose prose-sm max-w-none [&_p]:mb-2"
                dangerouslySetInnerHTML={{ __html: product.short_description }}
              />
            ) : (
              <p className="text-base text-muted-foreground leading-relaxed">
                Materiales y componentes profesionales certificados para instalaciones de fontanería,
                climatización y mantenimiento técnico en Murcia, Alicante y Almería.
              </p>
            )}

            {/* Add to Cart Area */}
            <div className="rounded-2xl border border-border bg-card/60 p-6 shadow-xs space-y-4">
              <VariableProductAddToCart
                product={product}
                onVariationResolved={(v) => setActiveImage(v?.featured_image_url ?? null)}
                buttonClassName="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 h-11 text-base shadow-sm"
                className="w-full"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-border/70 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Truck className="size-4 text-primary shrink-0" />
                  <span>Envío rápido en Murcia y alrededores</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-4 text-primary shrink-0" />
                  <span>Garantía oficial y servicio técnico</span>
                </div>
                <div className="flex items-center gap-2">
                  <Wrench className="size-4 text-primary shrink-0" />
                  <span>Opción de instalación profesional</span>
                </div>
                <div className="flex items-center gap-2">
                  <PackageCheck className="size-4 text-primary shrink-0" />
                  <span>Devolución garantizada 14 días</span>
                </div>
              </div>
            </div>

            {/* Meta: Categories & Tags */}
            <div className="space-y-3 pt-4 border-t border-border text-sm">
              {product.categories && product.categories.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">Categorías:</span>
                  <div className="flex flex-wrap gap-1.5">
                    {product.categories.map((cat, i) => (
                      <Badge
                        key={cat.id || i}
                        data-index={i}
                        variant="outline"
                        className="font-normal text-xs"
                      >
                        {cat.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {product.tags && product.tags.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">Etiquetas:</span>
                  <div className="flex flex-wrap gap-1.5">
                    {product.tags.map((tag, i) => (
                      <span
                        key={tag.id || i}
                        data-index={i}
                        className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-md"
                      >
                        #{tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs / Full Description Area */}
      <div className="mt-16 border-t border-border pt-10">
        <div className="flex items-center gap-4 border-b border-border pb-3">
          <Button
            type="button"
            variant={activeTab === "description" ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveTab("description")}
            className="rounded-lg font-semibold"
          >
            Descripción Completa
          </Button>
          <Button
            type="button"
            variant={activeTab === "specs" ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveTab("specs")}
            className="rounded-lg font-semibold"
          >
            Especificaciones y Soporte
          </Button>
        </div>

        <div className="py-6">
          {activeTab === "description" ? (
            <div className="max-w-4xl space-y-4">
              {product.long_description ? (
                <div
                  className="prose prose-neutral dark:prose-invert max-w-none text-foreground leading-relaxed [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1"
                  dangerouslySetInnerHTML={{ __html: product.long_description }}
                />
              ) : (
                <div className="space-y-4 text-foreground/90 leading-relaxed">
                  <p>
                    Este producto cumple con los más altos estándares de calidad requeridos para
                    instalaciones de fontanería, climatización, reformas y mantenimiento en viviendas y
                    comunidades. Todos nuestros equipos han sido seleccionados por el equipo técnico de
                    Servicios de Reparaciones en Totana para garantizar durabilidad y óptimo rendimiento.
                  </p>
                  <p>
                    Si necesita asistencia para su montaje o compatibilidad con sus tuberías y
                    sistemas de agua, nuestros especialistas están a su disposición para resolver
                    cualquier duda técnica o programar una instalación in situ.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="max-w-3xl space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="bg-card">
                  <CardContent className="p-4 space-y-1">
                    <span className="text-xs text-muted-foreground uppercase">Origen</span>
                    <p className="font-semibold text-sm">Distribución Oficial España</p>
                  </CardContent>
                </Card>
                <Card className="bg-card">
                  <CardContent className="p-4 space-y-1">
                    <span className="text-xs text-muted-foreground uppercase">Compatibilidad</span>
                    <p className="font-semibold text-sm">Estándar Europeo / Certificado UNE</p>
                  </CardContent>
                </Card>
                <Card className="bg-card">
                  <CardContent className="p-4 space-y-1">
                    <span className="text-xs text-muted-foreground uppercase">Servicio Técnico</span>
                    <p className="font-semibold text-sm">Servicios de Reparaciones Totana y Delegaciones</p>
                  </CardContent>
                </Card>
                <Card className="bg-card">
                  <CardContent className="p-4 space-y-1">
                    <span className="text-xs text-muted-foreground uppercase">Atención Directa</span>
                    <p className="font-semibold text-sm">Soporte telefónico y WhatsApp</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Related Products / Complementary Recommendations */}
      <div className="mt-16 border-t border-border pt-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground font-default">
              Productos Relacionados y Complementos
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Materiales y repuestos recomendados por los técnicos instaladores.
            </p>
          </div>
          <Button variant="outline" asChild size="sm">
            <Link to="/ShopPage" className="flex items-center gap-1.5">
              <span>Ver catálogo completo</span>
              <ChevronRight className="size-4" />
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {products.slice(1, 5).map((relProduct, i) => (
            <Card
              key={relProduct.id}
              data-index={i}
              className="group flex flex-col justify-between overflow-hidden rounded-2xl border border-border bg-card transition-all hover:shadow-md"
            >
              <Link to={relProduct.link} className="block overflow-hidden bg-muted/40 aspect-square">
                <img
                  src={relProduct.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1753482/img/placeholder.webp"}
                  alt={relProduct.title}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              </Link>
              <CardContent className="p-5 flex flex-col justify-between flex-1 space-y-4">
                <div className="space-y-1.5">
                  <Link to={relProduct.link}>
                    <h3 className="font-semibold text-foreground line-clamp-2 hover:underline text-sm leading-snug">
                      {relProduct.title}
                    </h3>
                  </Link>
                  <ProductPrice
                    product={relProduct}
                    className="text-base font-bold text-foreground"
                  />
                </div>
                <AddToCartButton
                  product={relProduct}
                  productId={relProduct.id}
                  variant="default"
                  size="sm"
                  text="Añadir"
                  className="w-full"
                  buttonClassName="bg-primary text-primary-foreground font-medium text-xs h-9 w-full shadow-xs"
                  showQuantity={false}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SingleProduct() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient?.getCurrentWPQueryParams
      ? window.wvcClient.getCurrentWPQueryParams()
      : undefined;

  return (
    <section
      id="single-product-template-section"
      data-nav="light"
      className="w-full bg-background text-foreground py-8 md:py-12 border-b border-border"
    >
      <EcommerceProductsProvider wp_query={currentWPQueryParams}>
        <ProductDetailConsumer />
      </EcommerceProductsProvider>
    </section>
  );
}